import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Tooltip } from 'primereact/tooltip';
import { Divider } from 'primereact/divider';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import {
    useCreateTriadResponseMutation,
    useGetTriadQuestionsQuery,
    useGetTriadScoresQuery,
    useGetTriadResponseQuery,
    useUpdateTriadMutation,
} from '../triad/triad.slice';
import { TRIAD_AUTO_SAVE_INTERVAL } from '../../../constants';
import '../triad/triad.scss';

interface TriadNewFormModalProps {
    setTriadPageNumber: React.Dispatch<React.SetStateAction<number>>;
    triadId: number | string;
    currTriad: any;
    onCancel: () => void;
    useSectionWeighting: boolean;
}

const COACHING_TYPES = ['Skill', 'Will'];
const OBSERVATION_METHODS = ['Live/Virtual', 'Recorded', 'Side by Side'];

const useV2Scores = (triadScores: any) => {
    return useMemo(() => {
        const scores = triadScores?.data?.filter((s: any) => s.triad_score_id >= 5) ?? [];
        const naScoreId = scores.find((s: any) => s.triad_score_name === 'Not Applicable')?.triad_score_id;
        return { v2Scores: scores, naScoreId };
    }, [triadScores]);
};

const useCoachingFormFields = (currTriad: any) => {
    const [coachingType, setCoachingType] = useState<string>(currTriad?.coaching_type ?? '');
    const [observationMethod, setObservationMethod] = useState<string>(currTriad?.observation_method ?? '');
    const [sessionDuration, setSessionDuration] = useState<string>(
        currTriad?.session_duration != null ? String(currTriad.session_duration) : '00'
    );
    useEffect(() => {
        if (currTriad) {
            if (currTriad.coaching_type) setCoachingType(currTriad.coaching_type);
            if (currTriad.observation_method) setObservationMethod(currTriad.observation_method);
            if (currTriad.session_duration != null) setSessionDuration(String(currTriad.session_duration));
        }
    }, [currTriad]);
    return { coachingType, setCoachingType, observationMethod, setObservationMethod, sessionDuration, setSessionDuration };
};

export const TriadSecondTabV2 = (props: TriadNewFormModalProps) => {
    const seedTriad =
        props.currTriad?.triad_id === props.triadId ? props.currTriad : null;

    const { coachingType, setCoachingType, observationMethod, setObservationMethod, sessionDuration, setSessionDuration } = useCoachingFormFields(seedTriad);
    const [finalQuestions, setFinalQuestions] = useState<any[]>([]);
    const [updateResponse, setUpdateResponse] = useState<boolean>(true);
    const [isAutoSave, setIsAutoSave] = useState(false);
    const autoSaveRef = useRef<any>();
    const tooltipRefs = useRef<{ [key: string]: any }>({});
    const lastSectionNameRef = useRef('');

    const loggedEmpNum = useSelector(selectCurrentEmpid);

    const { data: triadQuestions } = useGetTriadQuestionsQuery(undefined);
    const { data: triadScores } = useGetTriadScoresQuery(undefined);

    // Filter to only version 2 scores (IDs 5-8: Fully Met, Partially Met, Not Met, Not Applicable)
    const { v2Scores, naScoreId } = useV2Scores(triadScores);
    const { data: triadResponseData } = useGetTriadResponseQuery(
        { triadId: props?.triadId },
        { skip: !props?.triadId }
    );
    const [TriadResponse] = useCreateTriadResponseMutation();
    const [UpdateTriad] = useUpdateTriadMutation();

    // Build flat questions array from version 2 sections only
    useEffect(() => {
        if (!triadQuestions?.data?.length) return;
        console.log('all versions:', triadQuestions.data.map((v: any) => ({ id: v.triad_version_id, sections: v.sections?.length })));
        const versionData = triadQuestions.data.find((v: any) => v.triad_version_id === 2);
        console.log('versionData:', versionData);
        console.log('sections:', versionData?.sections);
        if (!versionData?.sections?.length) return;
        const questionsArray: any[] = [];
        versionData.sections.forEach((section: any) => {
            section.questions.forEach((question: any) => {
                questionsArray.push({
                    triadSectionId: section.triad_section_id,
                    triadSectionName: section.triad_section_name,
                    sectionWeight: section.section_weight ?? 0,
                    questionOrder: question.question_order,
                    triadQuestionDescription: question.triad_question_description,
                    triadQuestionId: question.triad_question_id,
                    scoreId: '',
                    scoreValue: '',
                    observation: '',
                    triadResponseId: null,
                    guidanceArray: question.triadScoreGuidance ?? [],
                });
            });
        });
        console.log('questionsArray built:', questionsArray.length);
        setFinalQuestions(questionsArray);
    }, [triadQuestions]);

    // Populate existing responses
    useEffect(() => {
        if (triadResponseData?.data?.length > 0 && (updateResponse || isAutoSave)) {
            let updateNeeded = false;
            const updatedQuestions = finalQuestions.map((question: any) => {
                const match = triadResponseData.data.find(
                    (r: any) => r.triad_question_id === question.triadQuestionId
                );
                if (match) {
                    if (
                        question.scoreId !== match.triad_score_id ||
                        question.observation !== match.observation_comment ||
                        question.triadResponseId !== match.triad_response_id
                    ) {
                        updateNeeded = true;
                    }
                    return {
                        ...question,
                        scoreId: match.triad_score_id,
                        observation: match.observation_comment,
                        triadResponseId: match.triad_response_id,
                    };
                }
                return question;
            });
            if (updateNeeded) setFinalQuestions(updatedQuestions);
            setIsAutoSave(false);
        }
    }, [triadResponseData]);

    const getValueByScoreId = (scoreId: string): number => {
        const point = v2Scores.find((score: any) => score.triad_score_id === scoreId);
        return point?.score_value ?? 0;
    };

    const buildPayload = (questions: any[]) =>
        questions.map((q: any) => ({
            triadId: props.triadId,
            triadQuestionId: q.triadQuestionId,
            triadScoreId: q.scoreId,
            observationComment: q.observation,
            triadResponseId: q.triadResponseId,
            value: getValueByScoreId(q.scoreId),
        }));

    // Auto-save
    const handleAutoSave = () => {
        let filtered = finalQuestions.filter(
            (q: any) => q.scoreId !== '' && (q.scoreId !== 4 ? q.observation !== '' : true)
        );
        if (triadResponseData?.data?.length > 0) {
            filtered = filtered.filter((q: any) => {
                const orig = triadResponseData.data.find(
                    (r: any) => r.triad_question_id === q.triadQuestionId
                );
                return (
                    !orig ||
                    q.scoreId !== orig.triad_score_id ||
                    q.observation !== orig.observation_comment ||
                    q.triadResponseId !== orig.triad_response_id
                );
            });
        }
        const data = buildPayload(filtered);
        if (data.length > 0) {
            TriadResponse(data)
                .unwrap()
                .then((res: any) => {
                    if (res.statusCode === 200 && res.data) setIsAutoSave(true);
                    else toast.error('Auto-save failed');
                })
                .catch((err: any) => toast.error(`${err} during Auto-save`));
        }
    };

    useEffect(() => {
        autoSaveRef.current = setInterval(handleAutoSave, TRIAD_AUTO_SAVE_INTERVAL);
        return () => clearInterval(autoSaveRef.current);
    }, [finalQuestions, triadResponseData]);

    const handleUpdates = (value: any, index: number, key: 'scoreId' | 'observation') => {
        setFinalQuestions((prev: any[]) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [key]: value };
            return updated;
        });
        setUpdateResponse(false);
    };

    const handleFinish = () => {
        if (!coachingType) {
            toast.error('Please select a Coaching Type');
            return;
        }
        if (!observationMethod) {
            toast.error('Please select an Observation Method');
            return;
        }

        const hasMissing = finalQuestions.some(
            (q: any) => q.scoreId === naScoreId ? false : q.scoreId === '' || q.observation === ''
        );
        if (hasMissing) {
            toast.error('Please fill all required fields');
            return;
        }

        // ── Diff against existing responses ──
        let filteredData: any[];
        if (triadResponseData?.data?.length > 0) {
            filteredData = finalQuestions.filter((q: any) => {
                const orig = triadResponseData.data.find(
                    (r: any) => r.triad_question_id === q.triadQuestionId
                );
                return (
                    !orig ||
                    q.scoreId !== orig.triad_score_id ||
                    q.observation !== orig.observation_comment ||
                    q.triadResponseId !== orig.triad_response_id
                );
            });
        } else {
            filteredData = [...finalQuestions];
        }

        // ── Short-circuit: nothing changed at all → go straight to tab 3 ──
        const noResponseChanges = updateResponse || filteredData.length === 0;
        const noCoachingChanges =
            coachingType === (props.currTriad?.coaching_type ?? '') &&
            observationMethod === (props.currTriad?.observation_method ?? '') &&
            Number(sessionDuration) === (props.currTriad?.session_duration ?? 0);

        if (noResponseChanges && noCoachingChanges) {
            props.setTriadPageNumber(6);
            return;
        }

        // ── Score calculation ──
        let avgValue: string | null = null;

        if (props.useSectionWeighting) {
            let weightedTotal = 0;
            let hasAnyScored = false;

            Object.entries(sectionMap).forEach(([, { weight, questions }]) => {
                const scoredQuestions = questions.filter(
                    (q: any) => q.scoreId !== '' && q.scoreId !== naScoreId
                );
                if (scoredQuestions.length === 0) return;

                const sectionEarned = scoredQuestions.reduce(
                    (acc: number, q: any) => acc + getValueByScoreId(q.scoreId), 0
                );
                const sectionMax = scoredQuestions.length * 2;

                weightedTotal += (sectionEarned / sectionMax) * weight;
                hasAnyScored = true;
            });

            avgValue = hasAnyScored ? weightedTotal.toFixed(4) : null;
        } else {
            const allData = buildPayload(finalQuestions);
            const totalValue = allData.reduce((acc: number, i: any) => acc + i.value, 0);
            const totalQs = allData.filter((i: any) => i.value > 0).length * 3;
            avgValue = totalQs > 0 ? (totalValue / totalQs).toFixed(4) : null;
        }

        if (!updateResponse && filteredData.length > 0) {
            TriadResponse(buildPayload(filteredData))
                .unwrap()
                .then((res: any) => {
                    if (res.statusCode === 200 && res.status) {
                        toast.success('Coaching Observations Response saved');
                    } else {
                        toast.error('Coaching Observations Response save failed');
                    }
                })
                .catch((err: any) => toast.error(err));
        }

        UpdateTriad({
            triadId: props.triadId,
            assessmentScore: avgValue,
            acknowledged: props?.currTriad?.acknowledged ?? false,
            coachingType,
            observationMethod,
            sessionDuration: Number(sessionDuration),
        })
            .unwrap()
            .then((res: any) => {
                if (res.statusCode === 200 && res.status) {
                    props.setTriadPageNumber(6);
                    toast.success('Coaching Observation saved successfully');
                } else {
                    toast.error('Coaching Observation save failed');
                }
            })
            .catch((err: any) => toast.error(err));
    };
    const isReadOnly =
        props?.currTriad?.employee_number === loggedEmpNum ||
        props?.currTriad?.acknowledged ||
        (props?.currTriad?.month && props?.currTriad?.month !== new Date().getMonth() + 1) ||
        (props?.currTriad?.year && props?.currTriad?.year !== new Date().getFullYear());

    // Group questions by section for section score display
    const sectionMap: { [key: string]: { weight: number; questions: any[] } } = {};
    finalQuestions.forEach((q: any) => {
        if (!sectionMap[q.triadSectionName]) {
            sectionMap[q.triadSectionName] = { weight: q.sectionWeight, questions: [] };
        }
        sectionMap[q.triadSectionName].questions.push(q);
    });

    const renderRadio = (question: any, index: number) => {
        if (!v2Scores.length) return <div />;
        return (
            <div className="flex justify-content-center mt-3">
                <div className="flex flex-column gap-4">
                    {v2Scores.map((item: any) => {
                        const tooltipId = `tooltip-score-${index}-${item.triad_score_id}`;
                        const guidance = question.guidanceArray?.find(
                            (g: any) => g.guidance_score_id === item.triad_score_id
                        );
                        return (
                            <div key={item.triad_score_id} className="flex align-items-center">
                                <div className="row">
                                    <div className="col-md-1">
                                        <RadioButton
                                            inputId={`${item.triad_score_id}-${index}`}
                                            name={String(question.triadQuestionId)}
                                            value={item.triad_score_id}
                                            onChange={(e) => handleUpdates(e.target.value, index, 'scoreId')}
                                            checked={question.scoreId === item.triad_score_id}
                                            disabled={isReadOnly}
                                            className={question.scoreId ? '' : 'p-invalid'}
                                        />
                                    </div>
                                    <div className="col-md-8">
                                        <label htmlFor={`${item.triad_score_id}-${index}`}>
                                            {item.triad_score_name}
                                        </label>
                                    </div>
                                    <div className="col-md-1">
                                        {guidance && (
                                            <>
                                                <div
                                                    id={tooltipId}
                                                    className="triad-question-circle"
                                                    onMouseEnter={(e) => tooltipRefs.current[tooltipId]?.show(e)}
                                                    onMouseLeave={(e) => tooltipRefs.current[tooltipId]?.hide(e)}
                                                >
                                                    <i className="pi pi-question" style={{ color: 'white' }} />
                                                </div>
                                                <Tooltip
                                                    ref={(el) => { tooltipRefs.current[tooltipId] = el; }}
                                                    target={`#${tooltipId}`}
                                                >
                                                    <p dangerouslySetInnerHTML={{ __html: guidance.triad_score_guidance_description }} />
                                                </Tooltip>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTab = () => {
        lastSectionNameRef.current = '';
        let globalIndex = 0;

        return (
            <>
                {/* ── Coaching Type / Observation Method / Session Duration ── */}
                <div className="row mb-4" style={{ alignItems: 'flex-start' }}>
                    <Divider />
                    <div className="col-md-3">
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Coaching Type:</div>
                        {COACHING_TYPES.map((type) => (
                            <div key={type} className="flex align-items-center mb-1">
                                <RadioButton
                                    inputId={`ct-${type}`}
                                    name="coachingType"
                                    value={type}
                                    onChange={(e) => setCoachingType(e.value)}
                                    checked={coachingType === type}
                                    disabled={isReadOnly}
                                />
                                <label htmlFor={`ct-${type}`} className="ms-2">{type}</label>
                            </div>
                        ))}
                    </div>
                    <div className="col-md-3">
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Observation Method</div>
                        {OBSERVATION_METHODS.map((method) => (
                            <div key={method} className="flex align-items-center mb-1">
                                <RadioButton
                                    inputId={`om-${method}`}
                                    name="observationMethod"
                                    value={method}
                                    onChange={(e) => setObservationMethod(e.value)}
                                    checked={observationMethod === method}
                                    disabled={isReadOnly}
                                />
                                <label htmlFor={`om-${method}`} className="ms-2">{method}</label>
                            </div>
                        ))}
                    </div>
                    <div className="col-md-3">
                        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Session Duration (mins.)</div>
                        <input
                            type="number"
                            min={0}
                            value={sessionDuration}
                            disabled={isReadOnly}
                            onChange={(e) => setSessionDuration(e.target.value)}
                            style={{
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                padding: '6px 10px',
                                width: '120px',
                                fontSize: '14px',
                            }}
                        />
                    </div>
                </div>
                <Divider />
                {/* ── Sections & Questions ── */}
                {Object.entries(sectionMap).map(([sectionName, { weight, questions }]) => {
                    const maxPoints = questions.length * 2;
                    const weightDisplay = Math.round(weight * 100);   // 0.15 → 15

                    // ── Live section score calculation ──
                    // N/A questions are excluded from both earned points and the max denominator,
                    // matching the same logic used in handleFinish.
                    const naCount = questions.filter((q: any) => q.scoreId === naScoreId).length;
                    const scoredQs = questions.filter(
                        (q: any) => q.scoreId !== '' && q.scoreId !== naScoreId
                    );
                    const earnedPoints = scoredQs.reduce(
                        (acc: number, q: any) => acc + getValueByScoreId(q.scoreId), 0
                    );
                    // Effective max shrinks as questions are marked N/A
                    const effectiveMax = (questions.length - naCount) * 2;
                    const displayMax = effectiveMax > 0 ? effectiveMax : maxPoints;
                    const sectionPct =
                        effectiveMax > 0
                            ? ((earnedPoints / effectiveMax) * weight * 100).toFixed(2)
                            : '0.00';

                    return (
                        <div key={sectionName} className="mb-4">
                            <h5 style={{ fontWeight: 'bold', marginTop: '16px' }}>
                                {sectionName} ({weightDisplay}%)
                            </h5>

                            {/* Column headers — only on first row */}
                            <div className="row" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                <div className="col-md-4">Criteria</div>
                                <div className="col-md-3">Score</div>
                                <div className="col-md-5">Comments</div>
                            </div>

                            {questions.map((question: any) => {
                                const idx = globalIndex++;
                                const rowClass = idx % 2 === 0 ? 'row-even' : 'row-odd';
                                return (
                                    <div key={question.triadQuestionId} className={`row ${rowClass}`} style={{ padding: '8px 0' }}>
                                        <div className="col-md-4" style={{ fontWeight: 'normal', alignSelf: 'center' }}>
                                            {question.triadQuestionDescription}
                                        </div>
                                        <div className="col-md-3">
                                            {renderRadio(question, idx)}
                                        </div>
                                        <div className="col-md-5">
                                            <DescriptionBox
                                                id={`description-${question.triadQuestionId}`}
                                                readOnly={isReadOnly}
                                                value={question.observation}
                                                responseDescription={question.observation}
                                                limit={2000}
                                                className={question.observation || question.scoreId === naScoreId ? '' : 'p-invalid'}
                                                onChange={(value: string) =>
                                                    handleUpdates(value, idx, 'observation')
                                                }
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* ── Section footer — live running total ── */}
                            <div style={{ marginTop: '8px', fontSize: '13px' }}>
                                <div><strong>Maximum Points:</strong> {maxPoints}</div>
                                <div>
                                    <strong>Section Score:</strong>{' '}
                                    ({earnedPoints} ÷ {displayMax}) × {weight} = {sectionPct}%
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* ── Footer Buttons ── */}
                <div className="button-container-triad" style={{ justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                    <Button
                        label="Cancel"
                        icon="pi pi-times-circle"
                        onClick={props.onCancel}
                        className="btn-secondary"
                        style={{ color: '#6366f1' }}
                    />
                    <Button
                        label="Return"
                        icon="pi pi-arrow-circle-left"
                        className="btn-secondary"
                        onClick={() => props.setTriadPageNumber(1)}
                        style={{ color: '#6366f1' }}
                    />
                    <Button
                        label="Next"
                        icon="pi pi-arrow-circle-right"
                        onClick={handleFinish}
                        className="btn-primary"
                        style={{ background: '#6366f1', border: 'none' }}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="p-10">
            {renderTab()}
        </div>
    );
};